import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CinePuzzle, LEVEL_CONFIG, createGame } from '../../utils/cineEngine';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

// ─── Seat SVG ─────────────────────────────────────────────────────────────────

interface SeatProps {
  state: 0 | 1 | 2;       // 0=unknown, 1=occupied, 2=confirmed-empty
  hasError: boolean;       // modo asistido: wrong fill
  isHinted: boolean;       // flash from hint
  onClick: () => void;
  onRightClick: (e: React.MouseEvent) => void;
  size: number;
  disabled: boolean;
}

function Seat({ state, hasError, isHinted, onClick, onRightClick, size, disabled }: SeatProps) {
  const back    = hasError  ? '#b45309'
                : isHinted  ? '#ca8a04'
                : state === 1 ? '#15803d'
                : state === 2 ? '#475569'
                : '#334155';
  const cushion = hasError  ? '#d97706'
                : isHinted  ? '#eab308'
                : state === 1 ? '#16a34a'
                : state === 2 ? '#64748b'
                : '#475569';
  const border  = hasError  ? '#92400e'
                : isHinted  ? '#713f12'
                : state === 1 ? '#14532d'
                : '#1e293b';

  return (
    <button
      onClick={onClick}
      onContextMenu={onRightClick}
      disabled={disabled}
      style={{ width: size, height: size, padding: Math.max(1, size * 0.06) }}
      className={`flex items-center justify-center transition-all duration-100 rounded
        ${!disabled ? 'hover:opacity-80 active:scale-95 cursor-pointer' : 'cursor-default'}
        ${isHinted ? 'animate-pulse' : ''}`}
    >
      <svg viewBox="0 0 24 26" style={{ width: size * 0.82, height: size * 0.82 }}>
        <rect x="1" y="1" width="22" height="15" rx="3" fill={back} stroke={border} strokeWidth="1.2" />
        <rect x="0" y="16" width="24" height="8" rx="2" fill={cushion} stroke={border} strokeWidth="1.2" />
        <rect x="0" y="13" width="3" height="5" rx="1" fill={back} />
        <rect x="21" y="13" width="3" height="5" rx="1" fill={back} />
        {state === 2 && (
          <>
            <line x1="7" y1="5" x2="17" y2="13" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
            <line x1="17" y1="5" x2="7" y2="13" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
          </>
        )}
        {state === 1 && (
          <>
            <circle cx="12" cy="7" r="3" fill="#bbf7d0" />
            <path d="M7 14 Q12 10 17 14" fill="#bbf7d0" />
          </>
        )}
      </svg>
    </button>
  );
}

// ─── Clue number ──────────────────────────────────────────────────────────────

function ClueNum({ n, fontSize }: { n: number; fontSize: number }) {
  return (
    <span
      style={{ fontSize, lineHeight: 1.1, minWidth: fontSize * 1.3 }}
      className="text-center font-bold text-white/90 select-none"
    >
      {n}
    </span>
  );
}

// ─── Difficulty list ──────────────────────────────────────────────────────────

const DIFFICULTIES = [1, 2, 3, 4, 5].map(d => ({ orden: d, ...LEVEL_CONFIG[d] }));

// ─── Main game ────────────────────────────────────────────────────────────────

export default function CineGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dif = parseInt(dificultad || '1');

  const { saveProgress: saveGameProgress } = useGameProgress('cine', dif);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  const [puzzle, setPuzzle]             = useState<CinePuzzle | null>(null);
  const [cells, setCells]               = useState<number[]>([]);
  const [history, setHistory]           = useState<number[][]>([]);
  const [histIdx, setHistIdx]           = useState(-1);
  const [elapsedTime, setElapsedTime]   = useState(0);
  const [moveCount, setMoveCount]       = useState(0);
  const [isCompleted, setIsCompleted]   = useState(false);
  const [showVictory, setShowVictory]   = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [hintIdx, setHintIdx]           = useState<number | null>(null);
  const [infoMsg, setInfoMsg]           = useState('');
  const [forceNew, setForceNew]         = useState(0);
  const [hoverRow, setHoverRow]         = useState<number | null>(null);
  const [hoverCol, setHoverCol]         = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [cellSize, setCellSize] = useState(32);

  // ─── Layout ───────────────────────────────────────────────────────────────

  const measureLayout = useCallback(() => {
    if (!containerRef.current || !puzzle) return;
    const { clientWidth: cw, clientHeight: ch } = containerRef.current;
    const { w, h, rowClues, colClues } = puzzle;
    const maxRowLen = Math.max(...rowClues.map(c => c.length), 1);
    const maxColLen = Math.max(...colClues.map(c => c.length), 1);
    const fs = Math.min(22, Math.max(13, Math.floor(Math.min(cw, ch) / 30)));
    const clueColW = maxRowLen * (fs * 1.6) + 10;
    const clueRowH = maxColLen * (fs * 1.4) + 8;
    const cs = Math.floor(Math.min((cw - clueColW - 4) / w, (ch - clueRowH - 4) / h));
    setCellSize(Math.max(cs, 18));
  }, [puzzle]);

  useEffect(() => {
    const ro = new ResizeObserver(measureLayout);
    if (containerRef.current) ro.observe(containerRef.current);
    measureLayout();
    return () => ro.disconnect();
  }, [measureLayout]);

  // ─── Generate ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const seedParam = searchParams.get('seed') ? parseInt(searchParams.get('seed')!) : undefined;
    const isNew = forceNew > 0;
    const p = createGame(dif, isNew ? undefined : seedParam);
    setPuzzle(p);
    setSearchParams({ seed: p.seed.toString() }, { replace: true });
    const empty = new Array(p.w * p.h).fill(0);
    setCells(empty);
    setHistory([[...empty]]);
    setHistIdx(0);
    setMoveCount(0);
    setElapsedTime(0);
    setIsCompleted(false);
    setShowVictory(false);
    setModoAsistido(false);
    setHintIdx(null);
    setInfoMsg('');
  }, [dif, forceNew]);

  // ─── Timer ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isCompleted) return;
    const t = setInterval(() => setElapsedTime(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [isCompleted]);

  // ─── History helpers ──────────────────────────────────────────────────────

  const pushHistory = (newCells: number[]) => {
    const snap = [...newCells];
    const newHist = history.slice(0, histIdx + 1);
    newHist.push(snap);
    if (newHist.length > 80) newHist.shift();
    setHistory(newHist);
    setHistIdx(newHist.length - 1);
  };

  const applyAndCheck = (newCells: number[], p: CinePuzzle) => {
    setCells(newCells);
    setMoveCount(m => m + 1);
    pushHistory(newCells);
    // Victory: every solution[i]=1 cell is marked 1, no solution[i]=0 cell is marked 1
    const won = p.solution.every((sol, i) =>
      sol === 1 ? newCells[i] === 1 : newCells[i] !== 1
    );
    if (won) {
      setIsCompleted(true);
      setShowVictory(true);
      saveGameProgress({ tiempo: elapsedTime, movimientos: moveCount + 1 });
      if (isInSession) saveSessionResult({ tiempo: elapsedTime, movimientos: moveCount + 1, completado: true });
    }
  };

  // ─── Cell interaction ─────────────────────────────────────────────────────

  const handleCellClick = (idx: number) => {
    if (isCompleted || !puzzle) return;
    setInfoMsg('');
    const newCells = [...cells];
    newCells[idx] = newCells[idx] === 0 ? 1 : newCells[idx] === 1 ? 2 : 0;
    applyAndCheck(newCells, puzzle);
  };

  const handleRightClick = (e: React.MouseEvent, idx: number) => {
    e.preventDefault();
    if (isCompleted || !puzzle) return;
    const newCells = [...cells];
    newCells[idx] = newCells[idx] === 2 ? 0 : 2;
    applyAndCheck(newCells, puzzle);
  };

  // ─── Controls ─────────────────────────────────────────────────────────────

  const onUndo = () => {
    if (histIdx <= 0) return;
    setCells([...history[histIdx - 1]]);
    setHistIdx(histIdx - 1);
  };

  const onRedo = () => {
    if (histIdx >= history.length - 1) return;
    setCells([...history[histIdx + 1]]);
    setHistIdx(histIdx + 1);
  };

  const onReset = () => {
    if (!puzzle || !window.confirm('¿Reiniciar el tablero?')) return;
    const empty = new Array(puzzle.w * puzzle.h).fill(0);
    setCells(empty);
    pushHistory(empty);
    setIsCompleted(false);
    setShowVictory(false);
    setHintIdx(null);
  };

  const onNewBoard = () => {
    setSearchParams({});
    setForceNew(f => f + 1);
  };

  const handleHint = () => {
    if (!puzzle || isCompleted) return;
    setInfoMsg('');
    const newCells = [...cells];

    // Priority 1: fix a wrong fill (marked 1 but should be 0)
    for (let i = 0; i < newCells.length; i++) {
      if (newCells[i] === 1 && puzzle.solution[i] === 0) {
        newCells[i] = 0;
        setHintIdx(i);
        setTimeout(() => setHintIdx(null), 1200);
        applyAndCheck(newCells, puzzle);
        return;
      }
    }

    // Priority 2: reveal a random correct filled cell not yet marked
    const missing: number[] = [];
    for (let i = 0; i < newCells.length; i++) {
      if (puzzle.solution[i] === 1 && newCells[i] !== 1) missing.push(i);
    }
    if (missing.length === 0) {
      setInfoMsg('¡Ya está todo correcto!');
      setTimeout(() => setInfoMsg(''), 2000);
      return;
    }
    const pick = missing[Math.floor(Math.random() * missing.length)];
    newCells[pick] = 1;
    setHintIdx(pick);
    setTimeout(() => setHintIdx(null), 1200);
    applyAndCheck(newCells, puzzle);
  };

  const handleShowSolution = () => {
    if (!puzzle || !window.confirm('¿Estás seguro? Esto mostrará la solución completa.')) return;
    const sol = Array.from(puzzle.solution).map(v => v === 1 ? 1 : 0);
    applyAndCheck(sol, puzzle);
  };

  // ─── Computed values ──────────────────────────────────────────────────────

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (!puzzle) return (
    <div className="h-full bg-gray-900 flex items-center justify-center text-white/60">
      🔄 Generando…
    </div>
  );

  const { w, rowClues, colClues } = puzzle;
  const maxRowLen = Math.max(...rowClues.map(c => c.length), 1);
  const maxColLen = Math.max(...colClues.map(c => c.length), 1);
  const fontSize  = Math.min(22, Math.max(13, Math.floor(cellSize * 0.58)));
  const clueColW  = maxRowLen * (fontSize * 1.6) + 10;
  const clueRowH  = maxColLen * (fontSize * 1.4) + 8;

  // Modo asistido: mark cells filled where solution says empty
  const errorSet = new Set<number>();
  if (modoAsistido) {
    cells.forEach((v, i) => { if (v === 1 && puzzle.solution[i] === 0) errorSet.add(i); });
  }

  return (
    <div className="h-full bg-gradient-to-b from-gray-900 to-slate-800 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* ── Top bar ── */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🎬 Cine</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />

        {/* Difficulty */}
        <div className="flex gap-1 flex-wrap">
          {DIFFICULTIES.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                if (dif === d.orden) onNewBoard();
                else navigate(`/juegos/cine/${d.orden}`);
              }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dif === d.orden ? 'bg-white text-gray-800' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icon} <span className="hidden sm:inline">{d.name}</span>
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Stats */}
        <span className="text-white/70 text-xs hidden sm:inline">⏱ {formatTime(elapsedTime)}</span>
        <span className="text-white/70 text-xs hidden sm:inline">🖱 {moveCount}</span>

        {/* Controls */}
        <div className="flex gap-1 items-center">
          {/* Deshacer */}
          <button onClick={onUndo} disabled={histIdx <= 0 || isCompleted} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          {/* Rehacer */}
          <button onClick={onRedo} disabled={histIdx >= history.length - 1 || isCompleted} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          {/* Pista */}
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          {/* Ver solución */}
          <button onClick={handleShowSolution} disabled={isCompleted} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          {/* Reiniciar */}
          <button onClick={onReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          {/* Nuevo tablero */}
          <button onClick={onNewBoard} title="Nuevo tablero"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          {/* Cómo se juega */}
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {/* Info message */}
      {infoMsg && (
        <div className="mx-3 mt-2 p-2 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-sm text-center">
          {infoMsg}
        </div>
      )}

      {/* Screen decoration */}
      <div className="shrink-0 mx-auto mt-1 mb-0.5 px-4" style={{ width: w * cellSize + clueColW + 16 }}>
        <div className="h-1.5 rounded-sm bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        <p className="text-white/20 text-center text-xs mt-0.5 tracking-widest uppercase select-none">pantalla</p>
      </div>

      {/* Puzzle area */}
      <div ref={containerRef} className="flex-1 min-h-0 flex items-center justify-center p-2">
        <div className="flex flex-col" style={{ gap: 0 }}>

          {/* Column clues row */}
          <div className="flex" style={{ marginBottom: 2 }}>
            <div style={{ width: clueColW, height: clueRowH, flexShrink: 0 }} />
            {colClues.map((clue, j) => (
              <div key={j} style={{ width: cellSize, height: clueRowH, flexShrink: 0 }}
                className={`flex flex-col items-center justify-end pb-1 transition-colors duration-75 ${hoverCol === j ? 'bg-yellow-400/10 rounded-t' : ''}`}>
                {clue.map((n, k) => <ClueNum key={k} n={n} fontSize={fontSize} />)}
              </div>
            ))}
          </div>

          {/* Rows */}
          {rowClues.map((clue, i) => (
            <div key={i} className="flex" style={{ marginBottom: 1 }}>
              {/* Row clue */}
              <div style={{ width: clueColW, height: cellSize, flexShrink: 0 }}
                className={`flex items-center justify-end pr-2 gap-1 transition-colors duration-75 ${hoverRow === i ? 'bg-yellow-400/10 rounded-l' : ''}`}>
                {clue.map((n, k) => <ClueNum key={k} n={n} fontSize={fontSize} />)}
              </div>

              {/* Seats */}
              {Array.from({ length: w }, (_, j) => {
                const idx = i * w + j;
                return (
                  <div key={j}
                    onMouseEnter={() => { setHoverRow(i); setHoverCol(j); }}
                    onMouseLeave={() => { setHoverRow(null); setHoverCol(null); }}
                    className={`transition-colors duration-75 ${(hoverRow === i || hoverCol === j) && !isCompleted ? 'bg-yellow-400/10' : ''}`}
                    style={{ width: cellSize, height: cellSize, flexShrink: 0 }}>
                    <Seat
                      state={cells[idx] as 0 | 1 | 2}
                      hasError={errorSet.has(idx)}
                      isHinted={hintIdx === idx}
                      onClick={() => handleCellClick(idx)}
                      onRightClick={(e) => handleRightClick(e, idx)}
                      size={cellSize}
                      disabled={isCompleted}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="shrink-0 flex items-center justify-center gap-4 pb-2 text-white/40 text-xs">
        <span>Clic = ocupar · 2× clic / clic derecho = vacío</span>
      </div>

      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{ tiempo: elapsedTime, movimientos: moveCount }}
      />

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Cine"
        gameIcon="🎬"
        objective="Descubre qué asientos están ocupados. Los números indican grupos de asientos consecutivos ocupados en cada fila y columna."
        rules={[
          'Cada número es un grupo de asientos consecutivos ocupados en esa fila o columna.',
          'Entre dos grupos siempre hay al menos un asiento vacío.',
          'El orden es de izquierda a derecha (filas) y de arriba a abajo (columnas).',
          'Clic = ocupado (verde). Doble clic o clic derecho = vacío confirmado (X). Triple clic = resetea.',
          'El modo asistido (escudo) resalta en naranja los asientos marcados incorrectamente.',
          'La pista (bombilla) corrige un error o revela un asiento ocupado al azar.',
        ]}
        howToPlay="Comienza por las filas y columnas con números más grandes — tienen más restricción. Usa los vacíos confirmados (X) para delimitar grupos. El modo asistido te ayuda a detectar errores sin revelar la solución."
        author="Variación de nonograma (Picross) con narrativa del Dr. Fernando Machuca Murga, neuropsicólogo."
      />
    </div>
  );
}
